import React, { useEffect, useContext, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Meter, Layer, Main, Text, NameValueList, Button, Page, PageContent, PageHeader, Grid, ResponsiveContext, DataChart, Stack, Spinner } from "grommet";
import { ChartCard } from "../components/admin/ChartCard";
import { Measure } from "../components/admin/Measure";
import { Legend } from "../components/admin/Legend";
import { Hpe, Group, UserAdd, Analytics, Notification } from "grommet-icons";
import { AdminDashboardCard } from "../components/admin/AdminDashboardCard";
import { AddResourceContent } from "../components/admin/AddResourcesContent";
import { adminServices, announcementService } from "../services";
import config from "../config";
import { isFullAdmin } from "../components/admin/roles";
import { NotificationContext } from "../components/admin/NotificationContext";
import { AnnouncementForm } from "./AdminAnnouncementsPage";

// theme graph tokens are distinct categorical hues and adapt to light/dark
const HPE_CHART_COLORS = ['graph-0', 'graph-1', 'graph-2', 'graph-3', 'graph-4', 'graph-5', 'graph-6', 'graph-7', 'graph-8', 'graph-9'];
const MAX_PIE_SLICES = HPE_CHART_COLORS.length;

// splits legend entries into two column-major halves (column one fills top-to-bottom first)
const splitIntoColumns = (items) => {
    const midpoint = Math.ceil(items.length / 2);
    return [items.slice(0, midpoint), items.slice(midpoint)];
};

export const AdminDashboard = () => {
    const size = useContext(ResponsiveContext);
    const session = useSelector((state) => {
        return state.session;
    });
    const fullAdmin = isFullAdmin(session.user);
    const { showNotification } = useContext(NotificationContext);
    const [isAnnouncementFormOpen, setIsAnnouncementFormOpen] = useState(false);

    const handleCreateAnnouncement = (data) =>
        announcementService.createAnnouncement(session.user.access, data).then(() => {
            showNotification("Announcement created", "normal");
            setIsAnnouncementFormOpen(false);
        });
    const [topDocumentsDetails, SetTopDocumentsDetails] = useState([])
    const [values, setValues] = useState([]);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [isLayer, SetIsLayer] = useState(false);
    const [pieChartData, setPieChartData] = useState([])
    const [totalNumberOfResources, setTotalNumbeOfResources] = useState(null)
    const competitorList = useSelector((state) => {
        return state.competitiveDash.competitors
    });
    const [userVisitData, setUserVisitData] = useState({ 'total_visits': 0, 'unique_visits': 0 })
    const [visitData, setVisitData] = useState([])
    const frequencyCardRef = useRef(null);
    const [frequencyCardHeight, setFrequencyCardHeight] = useState(null);

    useEffect(() => {
        const el = frequencyCardRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(([entry]) => {
            setFrequencyCardHeight(Math.round(entry.target.getBoundingClientRect().height));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const matchFrequencyHeight = frequencyCardHeight ? { height: `${frequencyCardHeight}px` } : {};

    useEffect(() => {
        adminServices.getTop5Resources(session.user.access).then((response) => {
            SetTopDocumentsDetails(response["top_resource"])
        })
        adminServices.getResourcesAnalytics(session.user.access).then((response) => {
            if (response.data) {
                setPieChartData(response.data)
                setTotalNumbeOfResources(response.total)
            }
        })
        adminServices.getTrafficData(session.user.access).then((response) => {
            setVisitData(response)
        })
        adminServices.getCompetitivePortalUsers(session.user.access).then((response) => {
            setUserVisitData({ 'total_visits': response.unique_visitors, 'unique_visits': response.total_visitors })
        })
        // adminServices.downloadTraficData(session.user.access).then((response) => {
        //     console.log({ response })
        // })

    }, [session.user.access]);
    useEffect(() => {
        if (pieChartData.length === 0) return;
        const sorted = pieChartData
            .map((datum) => ({
                key: datum.competitor__competitor_name,
                label: datum.competitor__competitor_name,
                value: parseInt(datum.resource_count, 10) || 0,
            }))
            .sort((a, b) => b.value - a.value);

        const top = sorted.slice(0, MAX_PIE_SLICES).map((v, i) => ({ ...v, color: HPE_CHART_COLORS[i] }));
        const rest = sorted.slice(MAX_PIE_SLICES);
        if (rest.length > 0) {
            top.push({
                key: "__other__",
                label: `Others (${rest.length})`,
                value: rest.reduce((sum, v) => sum + v.value, 0),
                color: "text-weak",
            });
        }
        setValues(top);
    }, [pieChartData])

    const handleResourcesDownload = async (access) => {
        const response = await fetch(`${config.apiUrl}/analytics/download-resources`,
            {
                headers: {
                    Authorization: "Bearer " + access
                },
            }
        );
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "all_resources.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();

    };
    const handleTrafficDataDownload = async (access) => {
        const response = await fetch(`${config.apiUrl}/analytics/download-traffic-data`,
            {
                headers: {
                    Authorization: "Bearer " + access
                },
            }
        );
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "traffic_data.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();

    };
    const handleResourceCountDataDownload = async (access) => {
        const response = await fetch(`${config.apiUrl}/analytics/download-visited-resources`,
            // {
            //     headers: {
            //         Authorization: "Bearer " + access
            //     },
            // }
        );
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "resources_vists_data.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();

    };
    return (

        <Page>
            <PageContent>
                <PageHeader title={"Admin Console"} actions={[<Box direction="row" gap="medium" alignSelf="end" >
                    <Button primary label="Add Document" onClick={() => { SetIsLayer(true) }} />
                    {fullAdmin && <Button secondary label="Add Announcement" onClick={() => setIsAnnouncementFormOpen(true)} />}
                </Box>]} />
                <Main align='center' gap="xlarge">
                    <Grid
                        columns={size === 'xlarge' || size === 'large' ? "25%" : '50%'} gap={"medium"}
                        rows={[["auto", "75%"]]}
                        pad='small'
                        fill="horizontal"
                        alignContent="start"
                    >
                        <ChartCard cardRef={frequencyCardRef} alignSelf="start" title={'Frequency'} subtitle={'Top 5 most visited documents'} onClick={() => {
                            handleResourceCountDataDownload(session.user.access)
                        }}>
                            < NameValueList pairProps={{ direction: 'column' }} gap={"xsmall"}>
                                {topDocumentsDetails.length > 0 ?
                                    topDocumentsDetails.map((document, index) => {
                                        return <React.Fragment key={index}>
                                            <Box>
                                                <Measure name={{ label: { label: 'Document Name', size: 'small' } }} value={{ value: document['resource__resource_title'], size: 'large' }} />
                                                <Measure name={{ label: { label: 'Total Visits', size: 'small' } }} value={{ value: document['visits'], size: 'large' }} />
                                            </Box>

                                        </React.Fragment>
                                    })

                                    : <Spinner />}
                            </NameValueList>
                        </ChartCard>
                        <ChartCard style={matchFrequencyHeight} title={'Application Traffic'} subtitle={'Number of users that logged in'} onClick={() => { handleTrafficDataDownload(session.user.access) }}>
                            <Box gap="medium">
                                <NameValueList pairProps={{ direction: 'column' }}>
                                    <Measure name={{ label: { label: 'Total', size: 'medium' } }} value={userVisitData.total_visits} />
                                    <Measure name={{ label: { label: 'Unique', size: 'medium' } }} value={userVisitData.unique_visits} />
                                </NameValueList>
                                <Box></Box>
                                {visitData.length > 0 ? <DataChart
                                    pad={"small"}
                                    data={visitData}
                                    series={
                                        [
                                            { property: 'month', label: 'Month', render: value => <Text>{value}</Text> },
                                            { property: 'visits', label: 'Visits', render: value => <Text>{value}</Text> },
                                            { property: 'android', label: "Android", render: value => <Text>{value}</Text> },
                                            { property: 'ios', label: "iOS", render: value => <Text>{value}</Text> },
                                            { property: 'web', label: "Web", render: value => <Text>{value}</Text> }
                                        ]
                                    }
                                    axis={{ x: { property: 'month', granularity: 'medium' }, y: { property: 'visits', granularity: 'medium' } }}
                                    chart={{ property: 'visits', thickness: 'hair', type: 'line' }}
                                    detail
                                    guide={{ y: { granularity: 'fine' } }}
                                    margin={{ right: 'medium' }}
                                /> : <Spinner alignSelf="center" />}

                            </Box>
                        </ChartCard>
                        <ChartCard style={matchFrequencyHeight} title={'Resources and Competitors'} subtitle={'Number of enabled documents'} onClick={() => { handleResourcesDownload(session.user.access) }}>
                            {totalNumberOfResources ? <NameValueList pairProps={{ direction: 'column' }}>
                                <Measure name={{ label: { label: 'Total Number of resources', size: 'medium' } }} value={totalNumberOfResources} />
                            </NameValueList> : <Spinner />}
                            {pieChartData.length > 0 ? <Box gap="xsmall" margin={{ top: "xsmall" }}>
                                <Box fill="horizontal" flex={false}>
                                    <Stack anchor="center">
                                        <Meter
                                            values={values.map((value, index) => ({
                                                label: value.label,
                                                value: value.value,
                                                color: value.color,
                                                highlight: hoveredIndex === index,
                                                onHover: (over) => setHoveredIndex(over ? index : null),
                                            }))}
                                            type="pie"
                                            size="full"
                                            thickness="medium"
                                        />
                                        {hoveredIndex !== null && values[hoveredIndex] && (
                                            <Box
                                                background="background-front"
                                                round="small"
                                                elevation="medium"
                                                pad={{ horizontal: 'small', vertical: 'xsmall' }}
                                                align="center"
                                            >
                                                <Text weight="bold" size="small">{values[hoveredIndex].label}</Text>
                                                <Text size="small">{values[hoveredIndex].value}</Text>
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>
                                <Box flex={false}>
                                    <Grid columns={['1/2', '1/2']} gap={{ column: "xsmall" }}>
                                        <Legend values={splitIntoColumns(values)[0]} />
                                        <Legend values={splitIntoColumns(values)[1]} />
                                    </Grid>
                                </Box>
                            </Box> : <Spinner />}
                        </ChartCard>

                        {data
                            .filter((datum) => !datum.fullAdminOnly || fullAdmin)
                            .map((datum) => <AdminDashboardCard key={datum.title} card={datum} height={{ min: "small" }} />)}
                    </Grid>
                    {isLayer ? <>
                        <Layer position="center" onEsc={() => { SetIsLayer(false) }}>
                            <AddResourceContent session={session} onCloseHandler={() => { SetIsLayer(false) }} competitorOptions={competitorList.data} />
                        </Layer>
                    </> : <></>}
                    {isAnnouncementFormOpen && (
                        <AnnouncementForm onCancel={() => setIsAnnouncementFormOpen(false)} onSubmit={handleCreateAnnouncement} />
                    )}
                </Main>
            </PageContent >
        </Page >
    );
};

export default AdminDashboard;

export const data = [
    {
        cta: "Edit",
        title: 'Competitive Data Manager',
        description: `Configure and add data/resources to competitive portal`,
        descriptionColor: 'text-strong',
        icon: <Hpe color="plain" />,
    },
    {
        cta: 'View',
        title: 'Customize User Announcements',
        fullAdminOnly: true,
        description:
            'Set announcements/Notifications for your applicaiton.',
        icon: <Notification color="purple!" />,
    },
    {
        cta: 'Get Analytics',
        title: 'Reports, Charts and analytics',
        description: `View and get your reports of the applicaiton`,
        icon: <Analytics color="blue!" />,
    },
    {
        cta: 'View',
        title: 'User Feedback',
        fullAdminOnly: true,
        description:
            'View all user feedback for your applciation.',
        icon: <Group color="status-warning" />,
    },
];